import axios from 'axios';


const BASE_URL = 'https://rent-server.create-site.gr/api';

// Error handling utilities
const handleApiError = (error) => {
  console.error('API Error:', error);
  
  if (error.response) {
    // Server responded with an error
    console.error('Server response error:', {
      status: error.response.status,
      data: error.response.data
    });    const message = error.response.data?.message || 'An error occurred';
    if (error.response.status === 401) {
      // Clear tokens on unauthorized
      localStorage.removeItem('adminToken');
      localStorage.removeItem('Authentication:');
      localStorage.removeItem('partnerToken');
      localStorage.removeItem('driverToken');
    }
    throw new Error(message);
  } else if (error.request) {
    // Request was made but no response received
    console.error('No response received:', error.request);
    throw new Error('Unable to connect to the server. Please check your internet connection.');
  } else {
    // Something happened in setting up the request
    console.error('Request setup error:', error.message);
    throw new Error(error.message || 'An error occurred while processing your request.');
  }
};

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Check for specific token types based on the request URL
    const employeeToken = localStorage.getItem('employeeToken');
    const adminToken = localStorage.getItem('adminToken');
    const partnerToken = localStorage.getItem('Authentication:') || localStorage.getItem('partnerToken');
    const driverToken = localStorage.getItem('driverToken');
    
    // Determine which token to use
    let token = null;
    
    // For employee routes, prioritize employee token
    if (config.url.includes('/employees') && employeeToken) {
      token = employeeToken;
      console.log('Using employee token for request to:', config.url);
    } else {
      // Otherwise use any available token with priority order
      token = adminToken || partnerToken || driverToken || employeeToken;
    }
    
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Don't set Content-Type for FormData, let the browser set it with the boundary
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => Promise.reject(handleApiError(error))
);

// Response interceptor
api.interceptors.response.use(
  (response) => {

    return response;
  },
  (error) => Promise.reject(handleApiError(error))
);

// Auth endpoints
export const auth = {
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);

      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw handleApiError(error);
    }
  },
  logout: async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('Authentication:');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  register: async (data) => {
    try {
      return await api.post('/auth/register', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getCurrentUser: async () => {
    try {
      return await api.get('/auth/me');
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Car endpoints
export const cars = {
  getAll: async () => {
    try {
      return await api.get('/cars');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAvailable: async (params) => {
    try {
      return await api.get('/cars/available', { params });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getTodayPrice: async (carId) => {
    try {
      // Get today's date in ISO format
      const today = new Date();
      const todayStr = today.toISOString();
      
      // Use the pricing endpoint from a single date (today to today)
      return await api.get(`/cars/${carId}/pricing`, { 
        params: { 
          startDate: todayStr,
          endDate: todayStr
        } 
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getById: async (id) => {
    try {
      return await api.get(`/cars/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  searchByLicensePlate: async (licensePlate) => {
    try {
      return await api.get(`/cars/search/license-plate/${encodeURIComponent(licensePlate)}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (formData) => {
    try {
      // Update headers for FormData
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      return await api.post('/cars', formData, config);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  update: async (id, data) => {
    try {
      return await api.put(`/cars/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateAvailability: async (id, data) => {
    try {
      return await api.patch(`/cars/${id}/availability`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/cars/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

    // Image management endpoints
  updateImage: async (id, imageFile) => {
    const formData = new FormData();
    formData.append('carImage', imageFile);
    
    const response = await api.put(`/cars/${id}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  deleteImage: async (id, imageIndex) => {
    const response = await api.delete(`/cars/${id}/image/${imageIndex}`);
    return response.data;
  },
  
  // Vehicle instance endpoints
  getVehicles: async (carId) => {
    try {
      return await api.get(`/cars/${carId}/vehicles`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getVehiclesFromCategory: async (categoryId) => {
    try {
      return await api.get(`/cars/category/${categoryId}/vehicles`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getCarModelFromVehicle: async (vehicleId) => {
    try {
      return await api.get(`/cars/vehicles/${vehicleId}/car`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getVehicleById: async (carId, vehicleId) => {
    try {
      return await api.get(`/cars/${carId}/vehicles/${vehicleId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  addVehicle: async (carId, vehicleData) => {
    try {
      return await api.post(`/cars/${carId}/vehicles`, vehicleData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateVehicle: async (carId, vehicleId, vehicleData) => {
    try {
      return await api.put(`/cars/${carId}/vehicles/${vehicleId}`, vehicleData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteVehicle: async (carId, vehicleId) => {
    try {
      return await api.delete(`/cars/${carId}/vehicles/${vehicleId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  // Service history endpoints
  getServiceHistory: async (carId, vehicleId) => {
    try {
      return await api.get(`/cars/${carId}/vehicles/${vehicleId}/service`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  addServiceRecord: async (carId, vehicleId, serviceData) => {
    try {
      // Handle FormData for file uploads
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      return await api.post(`/cars/${carId}/vehicles/${vehicleId}/service`, serviceData, config);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateServiceRecord: async (carId, vehicleId, serviceId, serviceData) => {
    try {
      return await api.put(`/cars/${carId}/vehicles/${vehicleId}/service/${serviceId}`, serviceData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteServiceRecord: async (carId, vehicleId, serviceId) => {
    try {
      return await api.delete(`/cars/${carId}/vehicles/${vehicleId}/service/${serviceId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  // Insurance endpoints
  updateInsurance: async (carId, vehicleId, insuranceData) => {
    try {
      return await api.put(`/cars/${carId}/vehicles/${vehicleId}/insurance`, insuranceData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getExpiringInsurance: async (days) => {
    try {
      return await api.get('/cars/insurance/expiring', { params: { days } });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getUpcomingMaintenance: async (days) => {
    try {
      return await api.get('/cars/maintenance/upcoming', { params: { days } });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  // KTEO, Emission Card and GPS endpoints
  updateKteo: async (carId, vehicleId, kteoData) => {
    try {
      return await api.put(`/cars/${carId}/vehicles/${vehicleId}/kteo`, kteoData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateEmissionCard: async (carId, vehicleId, emissionData) => {
    try {
      return await api.put(`/cars/${carId}/vehicles/${vehicleId}/emission`, emissionData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateGps: async (carId, vehicleId, gpsData) => {
    try {
      return await api.put(`/cars/${carId}/vehicles/${vehicleId}/gps`, gpsData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  addKteo: async (carId, vehicleId, kteoData) => {
    try {
      return await api.put(`/cars/${carId}/vehicles/${vehicleId}/kteo`, kteoData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  addEmission: async (carId, vehicleId, emissionData) => {
    try {
      return await api.put(`/cars/${carId}/vehicles/${vehicleId}/emission`, emissionData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  addGps: async (carId, vehicleId, gpsData) => {
    try {
      return await api.put(`/cars/${carId}/vehicles/${vehicleId}/gps`, gpsData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getExpiringKteo: async (days) => {
    try {
      return await api.get('/cars/kteo/expiring', { params: { days } });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getExpiringEmissionCards: async (days) => {
    try {
      return await api.get('/cars/emission/expiring', { params: { days } });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  // Vehicle attachment endpoints
  uploadAttachment: async (carId, vehicleId, attachmentData) => {
    try {
      // Use FormData for file upload
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      return await api.post(`/cars/${carId}/vehicles/${vehicleId}/attachments`, attachmentData, config);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAttachments: async (carId, vehicleId) => {
    try {
      return await api.get(`/cars/${carId}/vehicles/${vehicleId}/attachments`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateAttachment: async (carId, vehicleId, attachmentId, attachmentData) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      return await api.put(`/cars/${carId}/vehicles/${vehicleId}/attachments/${attachmentId}`, attachmentData, config);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteAttachment: async (carId, vehicleId, attachmentId) => {
    try {
      return await api.delete(`/cars/${carId}/vehicles/${vehicleId}/attachments/${attachmentId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Rental endpoints
export const rentals = {
  create: async (data) => {
    try {
      return await api.post('/rentals', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },  getAll: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination params
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add filter params
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.partner) queryParams.append('partner', params.partner);
      if (params.dateFilter) queryParams.append('dateFilter', params.dateFilter);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      
      const url = queryParams.toString() ? `/rentals?${queryParams.toString()}` : '/rentals';
      return await api.get(url);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getById: async (id) => {
    try {
      return await api.get(`/rentals/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getByPartner: async (partnerId) => {
    try {
      return await api.get(`/rentals/partner/${partnerId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },  update: async (id, data) => {
    try {
      console.log('Updating rental with data:', data);
      const response = await api.patch(`/rentals/${id}`, data);
      console.log('Rental update response:', response.data);
      return response;
    } catch (error) {
      console.error('Error updating rental:', error);
      throw handleApiError(error);
    }
  },
  updateStatus: async (id, status, cancellationReason = null) => {
    try {
      const data = { status };
      if (cancellationReason) {
        data.cancellationReason = cancellationReason;
      }
      return await api.patch(`/rentals/${id}/status`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updatePaymentStatus: async (id, isPaid) => {
    try {
      return await api.patch(`/rentals/${id}/payment`, { isPaid });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updatePaymentStatusNew: async (id, paymentStatus) => {
    try {
      return await api.patch(`/rentals/${id}/payment-status`, { paymentStatus });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  updateCommissionPayment: async (id, commissionPaid) => {
    try {
      return await api.patch(`/rentals/${id}/commission-payment`, { commissionPaid });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  getDurationDiscounts: async () => {
    try {
      return await api.get('/rentals/duration-discounts');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateDurationDiscount: async (term, discount) => {
    try {
      return await api.patch('/rentals/duration-discounts', { term, discount });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  delete: async (id) => {
    try {
      return await api.delete(`/rentals/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  // Manual AADE submission
  submitAADE: async (id) => {
    try {
      return await api.post(`/rentals/${id}/submit-aade`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
    // Manual Oxygen submission
  submitOxygen: async (id) => {
    try {
      return await api.post(`/rentals/${id}/submit-oxygen`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Assign rental to partner
  assignToPartner: async (id, partnerId) => {
    try {
      return await api.patch(`/rentals/${id}/assign-partner`, { partnerId });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getPreInvoicePdf: async (id) => {
    try {
      return await api.get(`/rentals/${id}/pre-invoice-pdf`, {
        responseType: 'blob'
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Coupon endpoints
export const coupons = {
  validate: async (code, orderAmount) => {
    try {
      return await api.get(`/coupons/validate/${code}`, { params: { orderAmount } });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAll: async () => {
    try {
      return await api.get('/coupons');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (data) => {
    try {
      return await api.post('/coupons', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  update: async (id, data) => {
    try {
      return await api.put(`/coupons/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/coupons/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deactivate: async (id) => {
    try {
      return await api.patch(`/coupons/${id}/deactivate`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Finance endpoints
export const finances = {
  getTransactions: async (params) => {
    try {
      return await api.get('/finances/transactions', { params });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  createTransaction: async (data) => {
    try {
      return await api.post('/finances/transactions', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateTransaction: async (id, data) => {
    try {
      return await api.put(`/finances/transactions/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteTransaction: async (id) => {
    try {
      return await api.delete(`/finances/transactions/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getBanks: async () => {
    try {
      return await api.get('/finances/banks');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  createBank: async (data) => {
    try {
      return await api.post('/finances/banks', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateBank: async (id, data) => {
    try {
      return await api.put(`/finances/banks/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteBank: async (id) => {
    try {
      return await api.delete(`/finances/banks/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getFunds: async () => {
    try {
      return await api.get('/finances/funds');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  createFund: async (data) => {
    try {
      return await api.post('/finances/funds', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateFund: async (id, data) => {
    try {
      return await api.put(`/finances/funds/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteFund: async (id) => {
    try {
      return await api.delete(`/finances/funds/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getSummary: async (params) => {
    try {
      return await api.get('/finances/summary', { params });
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// Partner endpoints
export const partners = {
  getAll: async () => {
    try {
      return await api.get('/partners');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getById: async (id) => {
    try {
      return await api.get(`/partners/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (data) => {
    try {
      return await api.post('/partners', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  update: async (id, data) => {
    try {
      return await api.put(`/partners/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/partners/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getRentals: async (partnerId) => {
    try {
      return await api.get(`/partners/${partnerId}/rentals`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Contact endpoints
export const contact = {
  submitContactForm: async (data) => {
    try {
      return await api.post('/contact/contact', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },  submitTransferRequest: async (data) => {
    try {
      // Log the size of the PDF data if it exists
      if (data.invoicePdf) {
        console.log(`Sending transfer request with PDF data: ~${Math.round((data.invoicePdf.length * 3/4) / 1024)}KB`);
      } else {
        console.log('Sending transfer request without PDF data');
      }
      
      return await api.post('/contact/transfer', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAllTransfers: async () => {
    try {
      return await api.get('/contact/transfers');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getTransferById: async (id) => {
    try {
      return await api.get(`/contact/transfers/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateTransferStatus: async (id, status) => {
    try {
      return await api.patch(`/contact/transfers/${id}/status`, { status });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteTransfer: async (id) => {
    try {
      return await api.delete(`/contact/transfers/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getTransferPriceEstimate: async (pickup, dropoff, date, serviceType, carType) => {
    try {
      return await api.get(`/contact/transfer-price?pickup=${pickup}&dropoff=${dropoff}&date=${date}&serviceType=${serviceType}&carType=${carType || 'car'}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getPartnerTransfers: async (partnerId) => {
    try {
      return await api.get(`/contact/partner-transfers/${partnerId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// Driver endpoints
export const drivers = {
  getAllDrivers: async () => {
    try {
      return await api.get('/drivers');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getDriverById: async (id) => {
    try {
      return await api.get(`/drivers/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  createDriver: async (data) => {
    try {
      return await api.post('/drivers', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateDriver: async (id, data) => {
    try {
      return await api.put(`/drivers/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  deleteDriver: async (id) => {
    try {
      return await api.delete(`/drivers/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  assignDriver: async (transferId, driverId, driverNotes) => {
    try {
      return await api.post(`/drivers/assign/${transferId}`, { 
        driverId, 
        driverNotes 
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  unassignDriver: async (transferId) => {
    try {
      return await api.post(`/drivers/unassign/${transferId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateDriverPassword: async (id, password) => {
    try {
      return await api.patch(`/drivers/${id}/password`, { password });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  login: async (credentials) => {
    try {
      return await api.post('/drivers/login', credentials);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getProfile: async () => {
    try {
      return await api.get('/drivers/profile/me');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAssignedTransfers: async () => {
    try {
      return await api.get('/drivers/transfers/assigned');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  completeTransfer: async (transferId) => {
    try {
      return await api.patch(`/drivers/transfers/${transferId}/complete`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  addTransferNotes: async (transferId, notes) => {
    try {
      return await api.patch(`/drivers/transfers/${transferId}/notes`, { notes });
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

export const transferPricing = {
  getAll: async () => {
    try {
      return await api.get('/transfer-pricing');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getById: async (id) => {
    try {
      return await api.get(`/transfer-pricing/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (data) => {
    try {
      return await api.post('/transfer-pricing', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  update: async (id, data) => {
    try {
      return await api.put(`/transfer-pricing/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/transfer-pricing/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  bulkUpdate: async (ids, updates) => {
    try {
      return await api.patch('/transfer-pricing/bulk-update', { ids, updates });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  bulkImport: async (data) => {
    try {
      return await api.post('/transfer-pricing/bulk', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  list: async (params) => {
    try {
      return await api.get('/transfer-pricing/list', { params });
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Tours endpoints
export const tours = {
  getAll: async () => {
    try {
      return await api.get('/tours');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getAllAdmin: async () => {
    try {
      return await api.get('/tours/admin');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getById: async (id) => {
    try {
      return await api.get(`/tours/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (data) => {
    try {
      return await api.post('/tours', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  update: async (id, data) => {
    try {
      return await api.put(`/tours/${id}`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/tours/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  toggleActive: async (id) => {
    try {
      return await api.patch(`/tours/${id}/toggle-active`);
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

export const tourBookings = {
  getAll: async () => {
    try {
      return await api.get('/tour-bookings');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getByTransfer: async (transferId) => {
    try {
      return await api.get(`/tour-bookings/transfer/${transferId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (data) => {
    try {
      return await api.post('/tour-bookings', data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  updateStatus: async (id, status) => {
    try {
      return await api.patch(`/tour-bookings/${id}/status`, { status });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/tour-bookings/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getPriceEstimate: async (tourId, date, persons) => {
    try {
      return await api.get(`/tour-bookings/price-estimate?tourId=${tourId}&date=${date}&persons=${persons}`);
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

export const employeeService = {
  // Employee authentication
  login: async (email, password) => {
    try {
      // Make sure we're using the correct employee login endpoint
      console.log('Calling employee login endpoint with:', { email });
      const response = await api.post('/employees/login', { email, password });
      
      // Store the token right away for future requests
      if (response.data && response.data.token) {
        localStorage.setItem('employeeToken', response.data.token);
      }
      
      return response;
    } catch (error) {
      console.error('Employee login error:', error);
      throw error;
    }
  },

  // Get current employee profile
  getProfile: async () => {
    return api.get('/employees/me');
  },
  
  // Update current employee's profile
  updateProfile: async (profileData) => {
    try {
      // Since we're handling FormData with possible file uploads, we need to handle Content-Type correctly
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      return await api.patch('/employees/me/update', profileData, config);
    } catch (error) {
      console.error('Error updating employee profile:', error);
      throw handleApiError(error);
    }
  },
  
  // Update current employee's password
  updatePassword: async (passwordData) => {
    try {
      return await api.patch('/employees/password', passwordData);
    } catch (error) {
      console.error('Error updating employee password:', error);
      throw handleApiError(error);
    }
  },
  
  // Get all employees (admin only)
  getAllEmployees: async () => {
    return api.get('/employees');
  },

  // Register a new employee (admin only)
  registerEmployee: async (employeeData) => {
    return api.post('/employees/register', employeeData);
  },

  // Update employee information (admin only)
  updateEmployee: async (id, employeeData) => {
    return api.put(`/employees/${id}`, employeeData);
  },

  // Delete an employee (admin only)
  deleteEmployee: async (id) => {
    return api.delete(`/employees/${id}`);
  },

  // Get employee handovers (admin only)
  getEmployeeHandovers: (id) => {
    return api.get(`/employees/${id}/handovers`);
  },

  // Test authentication
  testAuth: async () => {
    return api.get('/employees/test-auth');
  }
};

// Handover service
export const handoverService = {
  // Create a new handover
  createHandover: async (handoverData) => {
    try {
      console.log('API Layer - Creating handover with data:', handoverData);
      return await api.post('/handovers', handoverData);
    } catch (error) {
      console.error('API Layer - Error creating handover:', error);
      throw handleApiError(error);
    }
  },  // Upload documentation (photo, video, voice)
  uploadDocumentation: async (handoverId, formData) => {
    try {
      // Log what we're sending
      const documentType = formData.get('documentType');
      const file = formData.get('file');
      console.log(`API call: Uploading ${documentType} file ${file.name} (${file.type}) to handover ${handoverId}`);
      
      // For debugging - check what's actually in the FormData
      for (let [key, value] of formData.entries()) {
        console.log(`FormData contains: ${key} = ${typeof value === 'object' ? 'File: ' + value.name : value}`);
      }
      
      const response = await api.post(`/handovers/${handoverId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Log the response for debugging
      console.log(`Upload response for ${documentType} - Status: ${response.status}`, response.data);
      
      return response;
    } catch (error) {
      console.error(`Error uploading ${formData.get('documentType')} file:`, error);
      throw handleApiError(error);
    }
  },

  // Update handover details
  updateHandover: async (handoverId, updateData) => {
    return api.put(`/handovers/${handoverId}`, updateData);
  },
  // Get a specific handover
  getHandover: async (handoverId) => {
    try {
      console.log(`Fetching handover with ID: ${handoverId}`);
      const response = await api.get(`/handovers/${handoverId}`);
      
      // Log media counts from the response
      if (response.data) {
        const { photos = [], videos = [], voiceNotes = [] } = response.data;
        console.log(`Handover media counts from server:
          - Photos: ${photos.length}
          - Videos: ${videos.length}
          - Voice Notes: ${voiceNotes.length}
        `);
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching handover:', error);
      throw handleApiError(error);
    }
  },

  // Get handovers for a specific rental
  getHandoversByRental: async (rentalId) => {
    return api.get(`/handovers/rental/${rentalId}`);
  },

  // Get all handovers with filtering options
  getHandovers: async (filters = {}) => {
    const params = new URLSearchParams();
    
    // Add filters to query params
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    
    return api.get(`/handovers?${params.toString()}`);
  },
    // Delete a handover
  deleteHandover: async (handoverId) => {
    try {
      console.log(`Deleting handover with ID: ${handoverId}`);
      const response = await api.delete(`/handovers/${handoverId}`);
      console.log('Handover deletion response:', response.data);
      
      if (response.data && response.data.message) {
        console.log('Deletion success message:', response.data.message);
      }
      
      return response;
    } catch (error) {
      console.error('Error deleting handover:', error);
      throw handleApiError(error);
    }
  },  updateHandoverDetails: (id, data) => api.patch(`/handovers/${id}/details`, data),
  addDocumentation: (id, formData) => api.post(`/handovers/${id}/add-documentation`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  removeDocumentation: (id, type, index) => api.delete(`/handovers/${id}/documentation/${type}/${index}`),
  
  // Update money handover status
  updateMoneyStatus: async (handoverId, moneyHandedOver) => {
    try {
      console.log(`Updating money status for handover ${handoverId} to ${moneyHandedOver}`);
      const response = await api.patch(`/handovers/${handoverId}/money-status`, {
        moneyHandedOver
      });
      console.log('Money status update response:', response.data);
      return response;
    } catch (error) {
      console.error('Error updating money status:', error);
      throw handleApiError(error);
    }
  },
};

// AADE endpoints
export const aade = {
  testConnection: async () => {
    try {
      return await axios.get('https://rent-server.create-site.gr/api/aade/test-connection');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  submitRental: async (rentalId) => {
    try {
      return await axios.post(`https://rent-server.create-site.gr/api/aade/rentals/${rentalId}/submit`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  updateRental: async (rentalId, data) => {
    try {
      return await axios.post(`https://rent-server.create-site.gr/api/aade/rentals/${rentalId}/update`, data);
    } catch (error) {
      throw handleApiError(error);
    }
  },
    cancelRental: async (rentalId, entityVatNumber = null) => {
    try {
      let url = `https://rent-server.create-site.gr/api/aade/rentals/${rentalId}/cancel`;
      if (entityVatNumber) {
        url += `?entityVatNumber=${entityVatNumber}`;
      }
      return await axios.get(url);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  getRentalStatus: async (rentalId) => {
    try {
      return await axios.get(`https://rent-server.create-site.gr/api/aade/rentals/${rentalId}/status`);
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// Additional Options endpoints
export const additionalOptions = {
  getAll: async () => {
    try {
      const response = await api.get('/additional-options');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  getActive: async () => {
    try {
      const response = await api.get('/additional-options/active');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  getById: async (id) => {
    try {
      const response = await api.get(`/additional-options/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  create: async (data) => {
    try {
      const response = await api.post('/additional-options', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  update: async (id, data) => {
    try {
      const response = await api.put(`/additional-options/${id}`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  delete: async (id) => {
    try {
      const response = await api.delete(`/additional-options/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  toggle: async (id) => {
    try {
      const response = await api.patch(`/additional-options/${id}/toggle`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// Locations endpoints
export const locations = {
  getAll: async () => {
    try {
      const response = await api.get('/locations');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  getActive: async () => {
    try {
      const response = await api.get('/locations/active');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  getByRegion: async () => {
    try {
      const response = await api.get('/locations/by-region');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  getPopular: async (limit = 10) => {
    try {
      const response = await api.get(`/locations/popular?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  getById: async (id) => {
    try {
      const response = await api.get(`/locations/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  create: async (data) => {
    try {
      const response = await api.post('/locations', data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  update: async (id, data) => {
    try {
      const response = await api.put(`/locations/${id}`, data);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  delete: async (id) => {
    try {
      const response = await api.delete(`/locations/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  toggle: async (id) => {
    try {
      const response = await api.patch(`/locations/${id}/toggle`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  incrementPopularity: async (id) => {
    try {
      const response = await api.patch(`/locations/${id}/increment-popularity`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  recalculatePopularity: async () => {
    try {
      const response = await api.post('/locations/recalculate-popularity');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// Oxygen endpoints
export const oxygen = {
  getInvoices: async (params = {}) => {
    try {
      return await api.get('/oxygen/oxygen-invoices', { params });
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  getInvoiceById: async (invoiceId) => {
    try {
      return await api.get(`/oxygen/oxygen-invoices/${invoiceId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  
  downloadInvoicePdf: async (invoiceId, template = 'a4') => {
    try {
      return await api.get(`/oxygen/oxygen-invoices/${invoiceId}/pdf`, {
        params: { template },
        responseType: 'blob'
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },
    // Get payment methods for frontend
  getPaymentMethods: async () => {
    try {
      return await api.get('/oxygen/payment-methods');
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Admin management endpoints
export const admins = {
  getAll: async () => {
    try {
      return await api.get('/admins');
    } catch (error) {
      throw handleApiError(error);
    }
  },
  create: async (adminData) => {
    try {
      return await api.post('/admins', adminData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  update: async (id, adminData) => {
    try {
      return await api.put(`/admins/${id}`, adminData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  changePassword: async (id, passwordData) => {
    try {
      return await api.patch(`/admins/${id}/password`, passwordData);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  delete: async (id) => {
    try {
      return await api.delete(`/admins/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },
  getById: async (id) => {
    try {
      return await api.get(`/admins/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  }
};

// Stripe Payment Service - COMMENTED OUT FOR NOW
/*
const stripe = {
  // Create payment intent
  createPaymentIntent: (rentalData, amount, currency = 'eur') => 
    api.post('/stripe/create-payment-intent', { rentalData, amount, currency }),

  // Confirm payment
  confirmPayment: (paymentIntentId, rentalId) => 
    api.post('/stripe/confirm-payment', { payment_intent_id: paymentIntentId, rental_id: rentalId }),

  // Get payment status
  getPaymentStatus: (paymentIntentId) => 
    api.get(`/stripe/payment-status/${paymentIntentId}`),

  // Create refund
  createRefund: (paymentIntentId, amount, reason, rentalId) => 
    api.post('/stripe/refund', { 
      payment_intent_id: paymentIntentId, 
      amount, 
      reason, 
      rental_id: rentalId 
    }),

  // Calculate fees
  calculateFees: (amount) => 
    api.get(`/stripe/fees/${amount}`),

  // Get dashboard link
  getDashboardLink: () => 
    api.get('/stripe/dashboard-link'),
};
*/

export default {
  auth,
  cars,
  rentals,
  coupons,
  finances,
  partners,
  contact,
  drivers,
  transferPricing,
  tours,
  tourBookings,
  employeeService,
  handoverService,
  aade,
  additionalOptions,
  locations,
  oxygen,
  admins,
  // stripe, // Commented out for now
};
